import { useRef, useState } from "react";

import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Layout,
  message,
  Row,
  Space,
  Table,
} from "antd";

import { PrinterOutlined, TableOutlined } from "@ant-design/icons";
import axios from "axios";
import { sumBy } from "lodash";
import moment from "moment";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import RangeDatePickerFieldGroup from "../../commons/RangeDatePickerFieldGroup";
import SelectFieldGroup from "../../commons/SelectFieldGroup";
import { onChange } from "../../utils/form_utilities";
import { smallFormItemLayout } from "../../utils/Layouts";
import numberFormat from "../../utils/numberFormat";
import round from "../../utils/round";
import { addKeysToArray, onWarehouseSearch } from "../../utils/utilities";

const { Content } = Layout;

const transaction = "stocks-receiving";
const url = `/api/${transaction}/`;
const title = "Warehouse Receipt Items Report";

const initialValues = {
  period_covered: [moment(), moment()],
};
export default function WarehouseReceiptItemsReport() {
  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const auth = useSelector((state) => state.auth);
  const [state, setState] = useState(initialValues);
  const [options, setOptions] = useState({
    customers: [],
    warehouses: [],
  });
  const [loading, setLoading] = useState({
    search: false,
    download: false,
  });

  const report = useRef(null);

  const getReport = useCallback(
    ({ period_covered, is_excel = false, warehouse }) => {
      if (period_covered) {
        const form_data = {
          period_covered,
          warehouse,
          is_excel,
        };

        setLoading((prevState) => ({
          ...prevState,
          search: !is_excel,
          download: is_excel,
        }));

        axios
          .post(`${url}history-items`, form_data)
          .then((response) => {
            if (response.data) {
              if (is_excel) {
                setLoading((prevState) => ({
                  ...prevState,
                  download: false,
                }));
                return window.open(`/public/reports/${response.data.filename}`);
              }

              setRecords(response.data);
              setState((prevState) => ({
                ...prevState,
                total: response.data.total,
              }));
              setLoading((prevState) => ({
                ...prevState,
                search: false,
              }));
            }
          })
          .catch((err) => {
            message.error("There was an error processing your request");
          })
          .finally(() =>
            setLoading((prevState) => ({
              ...prevState,
              search: false,
              download: false,
            }))
          );
      } else {
        message.error("Please select period covered");
      }
    },
    []
  );

  const items_column = [
    {
      title: "Item",
      dataIndex: ["stock", "name"],
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      align: "right",
      width: 200,
      sorter: (a, b) => a.footer != 1 && a.quantity - b.quantity,
      render: (value) => numberFormat(value),
    },
    {
      title: "Amount",
      dataIndex: ["amount"],
      align: "right",
      width: 100,
      sorter: (a, b) => a.footer != 1 && a.amount - b.amount,
      render: (value) => numberFormat(value),
    },
  ];

  return (
    <Content className="content-padding">
      <div className="columns is-marginless">
        <div className="column">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Reports</Breadcrumb.Item>
            <Breadcrumb.Item className="text-color-yellow">
              {title}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          padding: 24,
          minHeight: 280,
          borderRadius: 10,
        }}
      >
        <span className="module-title">{title}</span>
        <Divider />
        <Row>
          <Col span={12}>
            <RangeDatePickerFieldGroup
              label="Period Covered"
              name="period_covered"
              value={state.period_covered}
              onChange={(dates) =>
                setState((prevState) => ({
                  ...prevState,
                  period_covered: dates,
                }))
              }
              error={errors.period_covered}
              formItemLayout={smallFormItemLayout}
            />
          </Col>
          <Col span={8}>
            <SelectFieldGroup
              label="Warehouse"
              value={state.warehouse?.name}
              onFocus={() => {
                onWarehouseSearch({ value: "", setOptions });
              }}
              onSearch={(value) => onWarehouseSearch({ value, setOptions })}
              onChange={(index) => {
                const warehouse = options.warehouses?.[index] || null;
                onChange({
                  key: "warehouse",
                  value: warehouse,
                  setState: setState,
                });
              }}
              formItemLayout={smallFormItemLayout}
              data={options.warehouses}
              column="name"
              error={errors.warehouse}
            />
          </Col>
        </Row>

        <Row>
          <Col span={12} offset={4}>
            <Form.Item className="field is-grouped">
              <Space>
                <Button
                  onClick={() => {
                    getReport({
                      ...state,
                    });
                  }}
                  loading={loading.search}
                  disabled={loading.search}
                >
                  Search
                </Button>
                <Button
                  icon={<TableOutlined />}
                  onClick={() => {
                    getReport({
                      ...state,
                      is_excel: true,
                    });
                  }}
                  loading={loading.download}
                  disabled={loading.download}
                >
                  Download to Excel
                </Button>
                <ReactToPrint
                  trigger={() => (
                    <Button type="primary" icon={<PrinterOutlined />}>
                      Print
                    </Button>
                  )}
                  bodyClass="print"
                  content={() => report.current}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <div ref={report}>
          <div>
            <div className="has-text-weight-bold">{title}</div>
            <div>
              Period Covered: {state.period_covered?.[0]?.format("MM/DD/YYYY")}{" "}
              to {state.period_covered?.[1]?.format("MM/DD/YYYY")}
            </div>
            <div>
              {state.branch?.company?.name || ""} - {state.branch?.name || ""}
            </div>
          </div>
          <Table
            dataSource={addKeysToArray([
              ...records,
              {
                footer: 1,
                quantity: sumBy(records, (o) => round(o.quantity)),
                amount: sumBy(records, (o) => round(o.amount)),
              },
            ])}
            columns={items_column}
            rowClassName={(record, index) => {
              if (record.footer === 1) {
                return "footer-summary has-text-weight-bold";
              }
            }}
            pagination={false}
          />
        </div>
      </div>
    </Content>
  );
}
